import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { tourEvent, useLessonAction } from '../tour/tourEvents';
import { extractFormulaRefs, compileFormula, formulaValueToString } from '../utils/formulaEval';

// ─────────────────────────────────────────────────────────────────────────────
// Formula column generator (Clay-style).
//   • A contenteditable prompt where typing "/" inserts a column chip.
//   • "Generate" turns the natural-language prompt into a formula (one cheap
//     LLM call) — then every row is computed locally, for free.
//   • A LIVE preview (client-side eval on the loaded sample) so the user can
//     confirm the output looks right before saving.
// ─────────────────────────────────────────────────────────────────────────────

export interface RefColumn {
  key: string; label: string; type?: string; isCustom: boolean;
  // Derived column computed from a row (e.g. "All Job Titles" across all roles).
  isVirtual?: boolean;
  virtual?: (row: any) => any;
}
export interface PreviewRow { id: number; name: string; row: any }

interface Props {
  projectId: number;
  token: string;
  entity: 'candidates' | 'companies';
  columns: RefColumn[];
  previewRows: PreviewRow[];
  onClose: () => void;
  onSaved: (info: {
    columnKey: string; columnLabel: string; formula: string; formulaPrompt: string; refs: string[];
  }) => void;
}

const FormulaColumnModal: React.FC<Props> = ({ projectId, token, entity, columns, previewRows, onClose, onSaved }) => {
  const [columnName, setColumnName] = useState('');
  const [prompt, setPrompt] = useState('');          // serialized ({{Label}} for chips)
  const [formula, setFormula] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formulaOpen, setFormulaOpen] = useState(true);
  const [refsOpen, setRefsOpen] = useState(false);

  const editorRef = useRef<HTMLDivElement | null>(null);
  const [slash, setSlash] = useState<{ open: boolean; query: string; index: number }>({ open: false, query: '', index: 0 });

  // label (lower) -> column, for resolving values + chip insertion.
  const byLabel = useMemo(() => {
    const m = new Map<string, RefColumn>();
    columns.forEach(c => m.set(c.label.toLowerCase(), c));
    return m;
  }, [columns]);

  const resolveValue = useCallback((row: any, label: string): any => {
    const col = byLabel.get(label.toLowerCase());
    if (!col) return undefined;
    if (col.isVirtual && col.virtual) return col.virtual(row);
    return col.isCustom ? row?.custom_data?.[col.key] : row?.[col.key];
  }, [byLabel]);

  // ── Preview (live, client-side) ────────────────────────────────────────────
  const refs = useMemo(() => extractFormulaRefs(formula), [formula]);
  const { preview, compileError } = useMemo(() => {
    if (!formula.trim()) return { preview: [] as { id: number; name: string; value: string }[], compileError: null as string | null };
    let compiled: ((v: any[]) => any) | null = null;
    let err: string | null = null;
    try { compiled = compileFormula(formula, refs); } catch (e: any) { err = e?.message || 'Invalid formula'; }
    const rows = (compiled ? previewRows.slice(0, 8).map(pr => {
      const values = refs.map(label => resolveValue(pr.row, label));
      return { id: pr.id, name: pr.name, value: formulaValueToString(compiled!(values)) };
    }) : []);
    return { preview: rows, compileError: err };
  }, [formula, refs, previewRows, resolveValue]);

  // ── Slash-to-insert-column chip editor ─────────────────────────────────────
  const serialize = useCallback(() => {
    const el = editorRef.current;
    if (!el) return '';
    let out = '';
    const walk = (node: Node) => {
      node.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          out += child.textContent || '';
        } else if (child instanceof HTMLElement) {
          if (child.dataset.col) out += `{{${child.dataset.col}}}`;
          else if (child.tagName === 'BR') out += '\n';
          else { if (child.tagName === 'DIV' && out && !out.endsWith('\n')) out += '\n'; walk(child); }
        }
      });
    };
    walk(el);
    return out.replace(/ /g, ' ');
  }, []);

  const onEditorInput = useCallback(() => {
    setPrompt(serialize());
    // Detect a "/" slash context immediately before the caret.
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { setSlash(s => ({ ...s, open: false })); return; }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || '').slice(0, range.startOffset);
      const m = text.match(/\/([\w .\-]*)$/);
      if (m) {
        const rect = range.getBoundingClientRect();
        setSlash({ open: true, query: m[1], index: 0 });
        // Position handled in render via a fixed anchor near caret.
        (window as any).__formulaCaretRect = rect;
        return;
      }
    }
    setSlash(s => ({ ...s, open: false }));
  }, [serialize]);

  const filteredCols = useMemo(() => {
    const q = slash.query.trim().toLowerCase();
    const list = q ? columns.filter(c => c.label.toLowerCase().includes(q)) : columns;
    return list.slice(0, 50);
  }, [slash.query, columns]);

  const insertChip = useCallback((col: RefColumn) => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const before = (node.textContent || '').slice(0, range.startOffset);
      const slashIdx = before.lastIndexOf('/');
      if (slashIdx !== -1) {
        // Remove the "/query" text.
        const r = document.createRange();
        r.setStart(node, slashIdx);
        r.setEnd(node, range.startOffset);
        r.deleteContents();
        // Build the chip.
        const chip = document.createElement('span');
        chip.dataset.col = col.label;
        chip.contentEditable = 'false';
        chip.className = 'inline-flex items-center gap-1 align-baseline px-1.5 py-0.5 mx-0.5 rounded border border-gray-300 bg-gray-50 text-xs text-gray-700';
        chip.textContent = col.label;
        r.insertNode(chip);
        // Trailing space so the caret sits after the chip.
        const space = document.createTextNode(' ');
        chip.after(space);
        const after = document.createRange();
        after.setStartAfter(space);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
      }
    }
    setSlash({ open: false, query: '', index: 0 });
    setPrompt(serialize());
    el.focus();
  }, [serialize]);

  const onEditorKeyDown = (e: React.KeyboardEvent) => {
    if (!slash.open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSlash(s => ({ ...s, index: Math.min(s.index + 1, filteredCols.length - 1) })); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSlash(s => ({ ...s, index: Math.max(s.index - 1, 0) })); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filteredCols[slash.index]) insertChip(filteredCols[slash.index]); }
    else if (e.key === 'Escape') { e.preventDefault(); setSlash(s => ({ ...s, open: false })); }
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const generate = async () => {
    const p = serialize().trim();
    if (!p) { setGenError('Describe what you want to calculate first.'); return; }
    setGenerating(true); setGenError(null);
    try {
      const res = await fetch(`/api/custom-columns/${projectId}/generate-formula`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: p, columns: columns.map(c => ({ label: c.label, type: c.type })) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate');
      setFormula(data.formula);
      setFormulaOpen(true);
    } catch (e: any) { setGenError(e.message || 'Failed to generate'); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    if (!formula.trim()) { setSaveError('Generate a formula first.'); return; }
    if (!columnName.trim()) { setSaveError('Give the column a name.'); return; }
    setSaving(true); setSaveError(null);
    try {
      const columnKey = `formula_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const res = await fetch(`/api/custom-columns/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          columnKey, columnLabel: columnName.trim(), columnType: 'formula',
          formula: formula.trim(), formulaPrompt: serialize(),
        }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed to save'); }
      tourEvent('formula-saved');
      onSaved({ columnKey, columnLabel: columnName.trim(), formula: formula.trim(), formulaPrompt: serialize(), refs });
    } catch (e: any) { setSaveError(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  useEffect(() => { editorRef.current?.focus(); }, []);

  // Learn tab — formulas lesson.
  useEffect(() => { tourEvent('formula-opened'); }, []);
  useEffect(() => { if (formula.trim()) tourEvent('formula-generated'); }, [formula]);
  useLessonAction('lesson-formula-example', () => {
    setColumnName('Mentions GenAI');
    if (editorRef.current) {
      editorRef.current.textContent = 'Yes if their headline mentions GenAI, otherwise No';
      onEditorInput();
    }
  });
  const caretRect = (window as any).__formulaCaretRect as DOMRect | undefined;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <span className="text-gray-500 italic">ƒ</span> Formula generator
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Describe what you want to calculate. A formula is generated once and applied to every row — no credits used.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Column name */}
          <div data-tour="formula-name">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Column name</label>
            <input
              value={columnName}
              onChange={e => setColumnName(e.target.value)}
              placeholder="e.g. Energy match"
              className="input text-sm w-full"
            />
          </div>

          {/* Prompt editor */}
          <div data-tour="formula-prompt">
            <label className="block text-xs font-semibold text-gray-700 mb-1">What would you like to calculate?</label>
            <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500/40">
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={onEditorInput}
                onKeyDown={onEditorKeyDown}
                className="min-h-[92px] max-h-[180px] overflow-y-auto px-3 py-2 text-sm text-gray-800 outline-none leading-7"
              />
              <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                <span className="text-[11px] text-gray-400">Type <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded">/</kbd> to insert a column</span>
                <button
                  data-tour="formula-generate"
                  onClick={generate}
                  disabled={generating}
                  className="btn-secondary text-xs inline-flex items-center gap-1.5"
                >
                  {generating && <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500" />}
                  {generating ? 'Generating…' : (formula ? 'Regenerate' : 'Generate')}
                </button>
              </div>
            </div>
            {genError && <p className="text-xs text-red-600 mt-1">{genError}</p>}
          </div>

          {/* Formula (collapsible) */}
          {formula && (
            <div className="border border-gray-200 rounded-lg">
              <button onClick={() => setFormulaOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-t-lg">
                <span className="text-sm font-semibold text-gray-800">Formula</span>
                <span className="text-gray-400">{formulaOpen ? '▲' : '▼'}</span>
              </button>
              {formulaOpen && (
                <div className="p-2">
                  <textarea
                    value={formula}
                    onChange={e => setFormula(e.target.value)}
                    spellCheck={false}
                    className="w-full h-24 font-mono text-xs text-gray-800 bg-white border border-gray-200 rounded p-2 outline-none resize-y"
                  />
                  {compileError && <p className="text-xs text-red-600 mt-1">{compileError}</p>}
                </div>
              )}
            </div>
          )}

          {/* Referenced columns (collapsible) */}
          {formula && refs.length > 0 && (
            <div className="border border-gray-200 rounded-lg">
              <button onClick={() => setRefsOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-t-lg">
                <span className="text-sm font-semibold text-gray-800">Referenced columns ({refs.length})</span>
                <span className="text-gray-400">{refsOpen ? '▲' : '▼'}</span>
              </button>
              {refsOpen && (
                <div className="p-2 space-y-1">
                  {refs.map(r => {
                    const known = byLabel.has(r.toLowerCase());
                    return (
                      <div key={r} className={`text-xs px-2 py-1.5 rounded border ${known ? 'border-gray-200 text-gray-700' : 'border-red-200 bg-red-50 text-red-600'}`}>
                        {r}{!known && ' — not a column in this project'}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Live preview */}
          {formula && !compileError && (
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-1">Preview <span className="font-normal text-gray-400">· first {preview.length} row{preview.length === 1 ? '' : 's'}, computed live</span></div>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {preview.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-gray-400">No rows loaded to preview.</div>
                ) : preview.map(p => (
                  <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="text-gray-600 truncate">{p.name || `#${p.id}`}</span>
                    <span className="font-semibold text-gray-900 whitespace-nowrap">{p.value === '' ? '—' : p.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-red-600">{saveError}</span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
            <button
              data-tour="formula-save"
              onClick={save}
              disabled={saving || !formula.trim() || !columnName.trim()}
              className="btn-primary text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {saving && <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
              {saving ? 'Saving…' : 'Save column'}
            </button>
          </div>
        </div>
      </div>

      {/* Slash column picker */}
      {slash.open && filteredCols.length > 0 && caretRect && (
        <div
          className="fixed z-[75] bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[180px] max-h-[220px] overflow-y-auto"
          style={{ top: Math.min(caretRect.bottom + 4, window.innerHeight - 230), left: Math.min(caretRect.left, window.innerWidth - 200) }}
          onClick={e => e.stopPropagation()}
        >
          {filteredCols.map((c, i) => (
            <button
              key={c.key}
              onMouseDown={e => { e.preventDefault(); insertChip(c); }}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${i === slash.index ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="text-gray-400 italic">T</span> {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormulaColumnModal;
